import { VOICE_CONFIG, SpeechRecognitionOptions } from "../types/audio";
import { getAzureToken } from "./azureTokenClient";

type SpeechSDKModule = typeof import("microsoft-cognitiveservices-speech-sdk");

let cachedSDK: SpeechSDKModule | null = null;

async function loadSpeechSDK(): Promise<SpeechSDKModule> {
  if (cachedSDK) return cachedSDK;
  cachedSDK = await import("microsoft-cognitiveservices-speech-sdk");
  return cachedSDK;
}

class AzureSpeechService {
  private recognizer: unknown = null;
  private isRecognizing = false;
  private silenceTimer: ReturnType<typeof setTimeout> | null = null;
  private noSpeechTimer: ReturnType<typeof setTimeout> | null = null;
  private sdk: SpeechSDKModule | null = null;

  private async ensureSDK(): Promise<SpeechSDKModule> {
    if (!this.sdk) this.sdk = await loadSpeechSDK();
    return this.sdk;
  }

  private async buildSpeechConfig(): Promise<unknown> {
    const sdk = await this.ensureSDK();
    const { token, region } = await getAzureToken();
    return sdk.SpeechConfig.fromAuthorizationToken(token, region);
  }

  async startRecognition(options: SpeechRecognitionOptions): Promise<void> {
    const {
      language,
      onInterimResult,
      onFinalResult,
      onError,
      onStart,
      onEnd,
      onTimeout,
      silenceTimeout = 3000,
      noSpeechTimeout,
    } = options;

    const effectiveNoSpeechTimeout = noSpeechTimeout ?? Math.max(silenceTimeout, 10000);

    if (this.isRecognizing) {
      return;
    }

    try {
      const sdk = await this.ensureSDK();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const config = (await this.buildSpeechConfig()) as any;
      const voiceConfig = VOICE_CONFIG[language];
      config.speechRecognitionLanguage = voiceConfig.locale;

      const audioConfig = sdk.AudioConfig.fromDefaultMicrophoneInput();
      this.recognizer = new sdk.SpeechRecognizer(config, audioConfig);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const recognizer = this.recognizer as any;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      recognizer.recognizing = (_sender: any, event: any) => {
        if (event.result.reason === sdk.ResultReason.RecognizingSpeech) {
          this.clearNoSpeechTimer();
          onInterimResult?.(event.result.text);
          this.resetSilenceTimer(silenceTimeout, onTimeout);
        }
      };

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      recognizer.recognized = (_sender: any, event: any) => {
        if (event.result.reason === sdk.ResultReason.RecognizedSpeech) {
          this.clearNoSpeechTimer();
          onFinalResult?.(event.result.text);
          this.resetSilenceTimer(silenceTimeout, onTimeout);
        } else if (event.result.reason === sdk.ResultReason.NoMatch) {
          // NoMatch means the SDK heard audio but couldn't transcribe it. Treat as
          // "speech attempted" — clear the pre-speech grace and fall back to silence.
          this.clearNoSpeechTimer();
          this.resetSilenceTimer(silenceTimeout, onTimeout);
        }
      };

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      recognizer.canceled = (_sender: any, event: any) => {
        if (event.reason === sdk.CancellationReason.Error) {
          const error = new Error(`Recognition error: ${event.errorDetails}`);
          onError?.(error);
        }
        this.stopRecognition();
      };

      recognizer.sessionStopped = () => {
        this.isRecognizing = false;
        onEnd?.();
        this.cleanupRecognizer();
      };

      await recognizer.startContinuousRecognitionAsync();
      this.isRecognizing = true;
      onStart?.();
      // First-speech gate: only arm the long no-speech grace at start. The short
      // silence timer doesn't begin until the first recognizing/recognized event.
      this.armNoSpeechTimer(effectiveNoSpeechTimeout, onTimeout);
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      onError?.(err);
      this.cleanupRecognizer();
      throw err;
    }
  }

  private resetSilenceTimer(timeout: number, onTimeout?: () => void): void {
    if (this.silenceTimer) clearTimeout(this.silenceTimer);
    this.silenceTimer = setTimeout(() => {
      if (this.isRecognizing) {
        this.stopRecognition();
        onTimeout?.();
      }
    }, timeout);
  }

  private armNoSpeechTimer(timeout: number, onTimeout?: () => void): void {
    this.clearNoSpeechTimer();
    this.noSpeechTimer = setTimeout(() => {
      if (this.isRecognizing) {
        this.stopRecognition();
        onTimeout?.();
      }
    }, timeout);
  }

  private clearNoSpeechTimer(): void {
    if (this.noSpeechTimer) {
      clearTimeout(this.noSpeechTimer);
      this.noSpeechTimer = null;
    }
  }

  async stopRecognition(): Promise<void> {
    if (this.silenceTimer) {
      clearTimeout(this.silenceTimer);
      this.silenceTimer = null;
    }
    this.clearNoSpeechTimer();
    if (this.recognizer && this.isRecognizing) {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        await (this.recognizer as any).stopContinuousRecognitionAsync();
      } catch (error) {
        console.error("Error stopping recognition:", error);
      }
      this.isRecognizing = false;
      this.cleanupRecognizer();
    }
  }

  private cleanupRecognizer(): void {
    if (this.recognizer) {
      (this.recognizer as { close: () => void }).close();
      this.recognizer = null;
    }
  }
}

export const azureSpeechService = new AzureSpeechService();
